import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, RotateCcw } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { sortByColumn, useTableSort } from "@/shared/hooks/useTableSort";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TableToolbar } from "../components/TableToolbar";
import { SortableTableHead } from "../components/SortableTableHead";
import { inputClass } from "../components/FormField";
import { getStoredAccountType } from "../auth";
import { useAdminInvoices } from "../hooks/useAdminInvoices";
import { DeadlineBadge, deadlineText } from "../components/DeadlineBadge";
import { cn } from "@/shared/lib/cn";
import { ExportMenu } from "../components/ExportMenu";

function formatAmount(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

export function ApprovedInvoicesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.invoices.approved;
  const { exportTable } = usePageActions();
  const { invoices: approvedInvoicesList, loading, error } = useAdminInvoices({
    mode: "approved",
  });

  const [topSearch, setTopSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const { sortKey, sortDirection, toggleSort } = useTableSort("displayId", "desc");

  const handleSort = (key: string) => {
    toggleSort(key);
    setPage(1);
  };

  const resetFilters = () => {
    setTopSearch("");
    setTableSearch("");
    setPage(1);
  };

  const filtered = useMemo(() => {
    return approvedInvoicesList.filter((invoice) => {
      const haystack =
        `${invoice.displayId} ${invoice.hospital} ${invoice.company} ${invoice.reference} ${invoice.submittedDate}`.toLowerCase();
      const byTopSearch = topSearch ? haystack.includes(topSearch.toLowerCase()) : true;
      const byTableSearch = tableSearch ? haystack.includes(tableSearch.toLowerCase()) : true;
      return byTopSearch && byTableSearch;
    });
  }, [approvedInvoicesList, topSearch, tableSearch]);

  const sorted = useMemo(
    () =>
      sortByColumn(filtered, sortKey, sortDirection, (invoice, key) => {
        if (key === "displayId") return invoice.displayId;
        if (key === "submittedDate") return invoice.submittedDate;
        if (key === "hospital") return invoice.hospital;
        if (key === "company") return invoice.insurer || invoice.company;
        if (key === "amount") return invoice.amount;
        if (key === "nature") return invoice.deadline?.due_at ?? "9999";
        if (key === "status") return labels.status;
        return "";
      }),
    [filtered, sortKey, sortDirection, labels.status],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const columnDefs = [
    { key: "displayId", label: labels.columns.id, sortable: true },
    { key: "submittedDate", label: labels.columns.date, sortable: true },
    { key: "hospital", label: labels.columns.hospital, sortable: true },
    { key: "company", label: "Assureur", sortable: true },
    { key: "nature", label: "Échéance", sortable: true },
    { key: "amount", label: labels.columns.amount, sortable: true },
    { key: "status", label: labels.columns.status, sortable: true },
    { key: "actions", label: labels.columns.actions, sortable: false },
  ] as const;

  const handleExportCsv = () => {
    exportTable(
      "factures-approuvees.csv",
      [
        labels.columns.id,
        labels.columns.date,
        labels.columns.hospital,
        "Assureur",
        "Échéance",
        labels.columns.amount,
        labels.columns.status,
      ],
      sorted.map((invoice) => [
        String(invoice.displayId),
        invoice.submittedDate,
        invoice.hospital,
        invoice.insurer || invoice.company,
        deadlineText(invoice.deadline),
        formatAmount(invoice.amount),
        labels.status,
      ]),
    );
  };

  return (
    <>
      <Breadcrumbs
        homeHref={getDashboardHomeHref()}
        items={[
          { label: t.safecheck.nav.invoices, href: "/dashboard/invoices" },
          { label: labels.title },
        ]}
      />

      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">{labels.title}</h1>
        {loading ? <p className="mt-1 text-sm text-muted">Chargement…</p> : null}
        {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
      </div>

      <div className="theme-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <input
            value={topSearch}
            onChange={(event) => {
              setTopSearch(event.target.value);
              setPage(1);
            }}
            placeholder={labels.searchPlaceholder}
            className={`${inputClass} w-full min-w-[200px] max-w-sm`}
          />
          <div className="flex items-center gap-2">
            <ExportMenu onExport={handleExportCsv} />
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent"
              aria-label={t.safecheck.reset}
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </div>

        <TableToolbar
          search={tableSearch}
          onSearchChange={(value) => {
            setTableSearch(value);
            setPage(1);
          }}
          searchPlaceholder="Rechercher…"
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showLabel={t.safecheck.show}
          entriesLabel={t.safecheck.entries}
        />

        <div className="overflow-x-auto">
          <table className="safecheck-table w-full min-w-[960px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columnDefs.map((column) => (
                  <SortableTableHead
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                    sortable={column.sortable}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((invoice, index) => (
                <tr
                  key={invoice.id}
                  className={cn(
                    "border-b border-border last:border-0 hover:bg-card-muted/30",
                    index % 2 === 1 ? "bg-card-muted/20" : "bg-card",
                  )}
                >
                  <td className="px-4 py-3 text-sm font-medium text-foreground">
                    <Link
                      to={`/dashboard/invoices/${invoice.displayId}`}
                      className="text-foreground transition-colors hover:text-primary"
                    >
                      {invoice.displayId}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{invoice.submittedDate}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{invoice.hospital}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{invoice.insurer || invoice.company}</td>
                  <td className="px-4 py-3">
                    <DeadlineBadge deadline={invoice.deadline} />
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-foreground">
                    {formatAmount(invoice.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-md bg-accent px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-accent-foreground">
                      {labels.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/dashboard/invoices/${invoice.displayId}`}
                      aria-label={labels.actions.view}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-primary transition-colors hover:border-accent hover:bg-accent/20"
                    >
                      <Eye size={16} />
                    </Link>
                  </td>
                </tr>
              ))}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm text-muted">
          <span>
            {labels.pagination.showing}{" "}
            {sorted.length === 0 ? 0 : start + 1} {labels.pagination.to}{" "}
            {Math.min(start + pageSize, sorted.length)} {labels.pagination.of}{" "}
            {sorted.length} {labels.pagination.entries}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.previous}
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNum) => (
              <button
                key={pageNum}
                type="button"
                onClick={() => setPage(pageNum)}
                className={cn(
                  "min-w-9 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                  pageNum === currentPage
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-muted-foreground hover:bg-accent/20 hover:border-accent",
                )}
              >
                {pageNum}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
              className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.next}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
